import { useEffect, useMemo, useState } from 'react';

import { loadExerciseFilters, persistExerciseFilters } from './helpers/storage';
import {
  DEFAULT_EXERCISE_FILTERS,
  IExerciseFilters,
  cloneExerciseFilters,
  getAppliedExerciseFiltersCount,
} from './helpers/types';

interface IUseExerciseFiltersParams {
  pageKey: string;
  onClose: () => void;
  normalizeFilters?: (filters: IExerciseFilters) => IExerciseFilters;
}

interface IUseExerciseFiltersResult {
  draftFilters: IExerciseFilters;
  appliedFilters: IExerciseFilters;
  appliedFiltersCount: number;
  setDraftFilters: (value: IExerciseFilters) => void;
  apply: () => void;
  cancel: () => void;
  reset: () => void;
}

function areExerciseFiltersEqual(
  left: IExerciseFilters,
  right: IExerciseFilters,
): boolean {
  return (
    left.categories.length === right.categories.length &&
    left.muscles.length === right.muscles.length &&
    left.equipment.length === right.equipment.length &&
    left.developedSkills.length === right.developedSkills.length &&
    left.categories.every(
      (value, index) => value === right.categories[index],
    ) &&
    left.muscles.every((value, index) => value === right.muscles[index]) &&
    left.equipment.every((value, index) => value === right.equipment[index]) &&
    left.developedSkills.every(
      (value, index) => value === right.developedSkills[index],
    )
  );
}

export function useExerciseFilters({
  pageKey,
  onClose,
  normalizeFilters,
}: IUseExerciseFiltersParams): IUseExerciseFiltersResult {
  function normalize(filters: IExerciseFilters): IExerciseFilters {
    const clonedFilters = cloneExerciseFilters(filters);
    return normalizeFilters ? normalizeFilters(clonedFilters) : clonedFilters;
  }

  const initialFilters = useMemo(() => loadExerciseFilters(pageKey), [pageKey]);
  const [appliedFilters, setAppliedFilters] = useState<IExerciseFilters>(
    normalize(initialFilters),
  );
  const [draftFilters, setDraftFiltersState] = useState<IExerciseFilters>(
    normalize(initialFilters),
  );

  useEffect(() => {
    const loadedFilters = normalize(loadExerciseFilters(pageKey));
    setAppliedFilters(cloneExerciseFilters(loadedFilters));
    setDraftFiltersState(cloneExerciseFilters(loadedFilters));
    persistExerciseFilters(pageKey, loadedFilters);
  }, [pageKey]);

  useEffect(() => {
    const normalizedApplied = normalize(appliedFilters);
    const normalizedDraft = normalize(draftFilters);
    const isAppliedChanged = !areExerciseFiltersEqual(
      normalizedApplied,
      appliedFilters,
    );
    const isDraftChanged = !areExerciseFiltersEqual(
      normalizedDraft,
      draftFilters,
    );

    if (isAppliedChanged) {
      setAppliedFilters(normalizedApplied);
      persistExerciseFilters(pageKey, normalizedApplied);
    }

    if (isDraftChanged) {
      setDraftFiltersState(normalizedDraft);
    }
  }, [normalizeFilters]);

  function setDraftFilters(value: IExerciseFilters) {
    setDraftFiltersState(normalize(value));
  }

  function apply() {
    const nextApplied = normalize(draftFilters);
    setAppliedFilters(nextApplied);
    persistExerciseFilters(pageKey, nextApplied);
    onClose();
  }

  function cancel() {
    setDraftFiltersState(cloneExerciseFilters(appliedFilters));
    onClose();
  }

  function reset() {
    const defaultFilters = cloneExerciseFilters(DEFAULT_EXERCISE_FILTERS);
    setAppliedFilters(defaultFilters);
    setDraftFiltersState(defaultFilters);
    persistExerciseFilters(pageKey, defaultFilters);
    onClose();
  }

  return {
    draftFilters,
    appliedFilters,
    appliedFiltersCount: getAppliedExerciseFiltersCount(appliedFilters),
    setDraftFilters,
    apply,
    cancel,
    reset,
  };
}
